/* eslint-disable react/jsx-max-props-per-line */
import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Alert, Grid, IconButton, InputAdornment, Typography } from '@mui/material';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import validate from 'validate.js';
import {
  ArquivoContainerV2,
  DefaultSelectV2,
  DialogDefaultV2,
  GeoPointMap,
  InputV2,
} from '../../../../components/v2';
import * as selectorsAuth from '../../../../store/auth/reducer';
import * as selectorsNsir from '../../../../store/nsir/reducer';
import * as selectorsNsmp from '../../../../store/nsmp/reducer';
import * as actionsNsmp from '../../../../store/nsmp/actions';
import * as Role from '../../../../constants/role';
import { isDigitacaoHabilitada } from '../../../../helpers/digitacaoAntecipada';
import {
  ORIGEM_ARQUIVO,
  RESULTADO_AUDITORIA,
  RESULTADO_AUDITORIA_LABEL,
  RESULTADO_AUDITORIA_OPTIONS,
  isFotoRfg,
  isFotoRfgExecucao,
  isFotoRfgInspecao,
  isResultadoCancelado,
  formatLatLongField,
  googleMapsUrl,
  parseCoordNumber,
  parseLatLongField,
} from '../../../../helpers/auditoriaFaixa';

const emptyValues = () => ({
  fotos: [],
  dataFotoDrone: moment().valueOf(),
  numeroServicoEmpreiteira: '',
  servicoInformado: '',
  servicoExecutado: '',
  servico: '',
  resultadoAuditoria: '',
  justificativa: '',
  observacao: '',
  latitude: '',
  longitude: '',
  coordenada: '',
});

const presence = (message) => ({ presence: { allowEmpty: false, message } });

const AuditoriaFaixaModal = ({
  open,
  onCancel,
  onConfirm,
  onConfirmAndNext,
  selectedId,
  nsmps,
  nsmpSelected,
  loadNsmpById,
  readOnly,
  userRole,
  userId,
  nsirStatus,
  nsirData,
  nsirTitle,
  onNext,
  onPrevious,
  hasNext,
  hasPrevious,
  carouselPosition,
}) => {
  const [canEditNsmp, setCanEditNsmp] = useState(false);
  const [readOnlyLocal, setReadOnlyLocal] = useState(readOnly);
  const [editFiles, setEditFiles] = useState(false);
  const [errors, setErrors] = useState({});
  const [values, setValues] = useState(emptyValues());

  const isAdmin = () => userRole === Role.ADMIN || userRole === Role.SUPER_ADMIN;

  const isDigitacaoCamposAtivos = () =>
    isDigitacaoHabilitada({
      status: nsirStatus,
      digitadorId: nsirData?.digitadorId,
      userId,
      userRole,
      digitacaoAntecipada: nsirData?.digitacaoAntecipada,
    });

  useEffect(() => {
    if (!open) return;
    setCanEditNsmp(false);
    setEditFiles(false);
    if (isDigitacaoCamposAtivos()) {
      setReadOnlyLocal(false);
      return;
    }
    if (selectedId) {
      setReadOnlyLocal(true);
      return;
    }
    setReadOnlyLocal(false);
  }, [open, selectedId, readOnly, nsirStatus, userId, userRole, nsirData?.digitadorId, nsirData?.digitacaoAntecipada]);

  useEffect(() => {
    if (selectedId) loadNsmpById(selectedId);
  }, [selectedId, loadNsmpById]);

  useEffect(() => {
    if (nsmpSelected && nsmpSelected.id) {
      setValues({
        ...emptyValues(),
        ...nsmpSelected,
        fotos: nsmpSelected.fotos || [],
      });
    }
  }, [nsmpSelected]);

  const clearForm = () => {
    setCanEditNsmp(false);
    setEditFiles(false);
    setErrors({});
    setValues(emptyValues());
  };

  useEffect(() => {
    if (!open) clearForm();
  }, [open]);

  const cancelado = isResultadoCancelado(values.resultadoAuditoria);

  const isReadOnly = canEditNsmp
    ? false
    : isDigitacaoCamposAtivos()
      ? false
      : readOnlyLocal;

  const handleChange = (name, value) => {
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const fotosRfg = useMemo(() => {
    const inspecao = (values.fotos || []).filter(isFotoRfgInspecao);
    const execucao = (values.fotos || []).filter(isFotoRfgExecucao);
    let index = 1;
    const withLabel = (foto, tipo) => ({
      ...foto,
      nome: `Arquivo ${index++} (${tipo})`,
    });
    return [
      ...inspecao.map((foto) => withLabel(foto, 'Inspeção')),
      ...execucao.map((foto) => withLabel(foto, 'Execução')),
    ];
  }, [values.fotos]);
  const fotosInspetor = useMemo(
    () => (values.fotos || []).filter((foto) => !isFotoRfg(foto)),
    [values.fotos]
  );

  const mapPoints = useMemo(() => {
    const lat = parseCoordNumber(values.latitude);
    const lng = parseCoordNumber(values.longitude);
    if (lat == null || lng == null) return [];
    return [{
      id: values.id || 'preview',
      lat,
      lng,
      title: values.codigo
        ? `Serviço ${values.codigo}`
        : 'Serviço',
    }];
  }, [values.id, values.latitude, values.longitude, values.codigo]);

  const getCodigo = () => {
    if (values && values.id) return values.codigo;
    let codigo = Array.isArray(nsmps) && nsmps.length > 0
      ? nsmps.filter((n) => n.fotoInicio !== true && n.fotoFim !== true && n.analiseRisco !== true).length + 1
      : 1;
    while ((`${codigo}`).length < 3) codigo = `0${codigo}`;
    return codigo;
  };

  const runValidation = () => {
    const schema = {};
    if (!cancelado) {
      schema.servico = presence('é obrigatório');
      schema.resultadoAuditoria = presence('é obrigatório');
      if (values.resultadoAuditoria === RESULTADO_AUDITORIA.INVALIDO) {
        schema.justificativa = presence('é obrigatória quando o resultado é não conforme');
      }
    }
    const nextErrors = { ...validate(values, schema) };
    if (!cancelado) {
      const lat = parseCoordNumber(values.latitude);
      const lng = parseCoordNumber(values.longitude);
      if (lat == null || lng == null) {
        nextErrors.latitude = ['Latitude / longitude é obrigatória'];
      }
    }
    setErrors(nextErrors || {});
    return !nextErrors || Object.keys(nextErrors).length === 0;
  };

  const buildPayload = () => ({
    ...values,
    codigo: getCodigo(),
    numeroServicoEmpreiteira: values.numeroServicoEmpreiteira || getCodigo(),
    tipoNsmp: values.tipoNsmp || 'SERVICO',
    dataFotoDrone: values.dataFotoDrone || moment().valueOf(),
    updateFiles: editFiles,
    resultadoAuditoria: cancelado
      ? RESULTADO_AUDITORIA.CANCELADO
      : (values.resultadoAuditoria || null),
  });

  const onConfirmLocal = () => {
    if (runValidation()) onConfirm(buildPayload());
  };

  const onConfirmAndNextLocal = () => {
    if (runValidation()) onConfirmAndNext(buildPayload());
  };

  const onCancelLocal = () => {
    if (canEditNsmp) {
      setCanEditNsmp(false);
      setReadOnlyLocal(true);
      setEditFiles(false);
      if (nsmpSelected && nsmpSelected.id) {
        setValues({ ...emptyValues(), ...nsmpSelected, fotos: nsmpSelected.fotos || [] });
      }
      return;
    }
    clearForm();
    onCancel();
  };

  const mergeFotos = (rfg, inspetor) => [...rfg, ...inspetor];

  const buildHeaderActions = () => {
    const actions = [];
    if (carouselPosition) {
      actions.push({ title: '◄', onClick: onPrevious, disabled: !hasPrevious });
      actions.push({ title: `${carouselPosition.current} / ${carouselPosition.total}`, onClick: () => {}, disabled: true });
      actions.push({ title: '►', onClick: onNext, disabled: !hasNext });
    }
    if (isAdmin() && readOnlyLocal && selectedId && !isDigitacaoCamposAtivos()) {
      actions.push({
        onClick: () => { setReadOnlyLocal(false); setCanEditNsmp(true); },
        title: 'Editar',
      });
    }
    return actions.length > 0 ? actions : null;
  };

  const extraActions = hasNext && !isReadOnly
    ? [{ label: 'Salvar e Avançar', onClick: onConfirmAndNextLocal }]
    : null;

  const resultadoOptions = cancelado
    ? [{ id: RESULTADO_AUDITORIA.CANCELADO, nome: RESULTADO_AUDITORIA_LABEL.CANCELADO }]
    : RESULTADO_AUDITORIA_OPTIONS;

  const titleNumero = getCodigo();
  const mapsHref = googleMapsUrl(values.latitude, values.longitude);
  const handleLatLongChange = (event) => {
    const parsed = parseLatLongField(event.target.value);
    setValues((prev) => ({
      ...prev,
      latitude: parsed.latitude,
      longitude: parsed.longitude,
    }));
  };

  return (
    <DialogDefaultV2
      open={open}
      handlerCancel={onCancelLocal}
      handlerConfirm={onConfirmLocal}
      title={`${nsirTitle} > Serviço ${titleNumero}`}
      confirmMessage="Salvar"
      confirmAutoFocus={false}
      fullWidth
      maxWidth="md"
      readOnly={isReadOnly}
      headerAction={buildHeaderActions()}
      extraActions={extraActions}
    >
      <Grid container spacing={1} sx={{ pt: 1 }}>
        {cancelado && (
          <Grid size={{ xs: 12 }}>
            <Alert severity="info">
              Serviço cancelado no RFG da terceirizada — registro informativo, sem conferência de campo.
            </Alert>
          </Grid>
        )}
        <Grid size={{ xs: 12, sm: 3 }}>
          <InputV2
            label="Nº do serviço (empreiteira)"
            name="codigo"
            isRequired
            value={getCodigo() || ''}
            onChange={() => {}}
            disabled
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <DefaultSelectV2
            label="Resultado da auditoria"
            name="resultadoAuditoria"
            isRequired={!cancelado}
            showEmpty={!cancelado}
            error={!!(errors && errors.resultadoAuditoria)}
            options={resultadoOptions}
            value={values.resultadoAuditoria || ''}
            onSelect={(event) => handleChange(event.target.name, event.target.value)}
            disabled={isReadOnly || cancelado}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <InputV2
            label="Latitude / Longitude"
            name="latLong"
            isRequired={!cancelado}
            error={!!(errors && errors.latitude)}
            helperText={errors && errors.latitude ? errors.latitude[0] : ''}
            value={formatLatLongField(values.latitude, values.longitude)}
            onChange={handleLatLongChange}
            disabled={isReadOnly}
            inputProps={{
              endAdornment: mapsHref ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    component="a"
                    href={mapsHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Abrir no Google Maps"
                  >
                    <MapOutlinedIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </Grid>
        {mapPoints.length > 0 && (
          <Grid size={{ xs: 12 }}>
            <GeoPointMap
              points={mapPoints}
              active={open}
              height={180}
              expandable
            />
          </Grid>
        )}
        <Grid size={{ xs: 12 }}>
          <InputV2
            label="Serviço informado"
            name="servico"
            isRequired={!cancelado}
            error={!!(errors && errors.servico)}
            value={values.servico || ''}
            onChange={(event) => handleChange(event.target.name, event.target.value)}
            multiline
            rows={2}
            disabled={isReadOnly}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <InputV2
            label="Observação empreiteira"
            name="observacao"
            value={values.observacao || ''}
            onChange={(event) => handleChange(event.target.name, event.target.value)}
            multiline
            rows={2}
            disabled={isReadOnly}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <InputV2
            label="Observação"
            name="justificativa"
            isRequired={values.resultadoAuditoria === RESULTADO_AUDITORIA.INVALIDO}
            error={!!(errors && errors.justificativa)}
            value={values.justificativa || ''}
            onChange={(event) => handleChange(event.target.name, event.target.value)}
            multiline
            rows={2}
            disabled={isReadOnly}
          />
        </Grid>
        {fotosRfg.length > 0 && (
          <Grid size={{ xs: 12 }}>
            <ArquivoContainerV2
              headerTitle="Fotos do RFG"
              arquivos={fotosRfg}
              hasAdd={false}
              showDelete={false}
              disabled
              onSave={() => {}}
            />
          </Grid>
        )}
        <Grid size={{ xs: 12 }}>
          <ArquivoContainerV2
            headerTitle="Fotos da inspeção"
            arquivos={fotosInspetor}
            hasAdd={!isReadOnly && !cancelado}
            showDelete={!isReadOnly}
            disabled={isReadOnly || cancelado}
            onSave={(fotos) => {
              setEditFiles(true);
              const withOrigem = (fotos || []).map((foto) => (
                foto.origem ? foto : { ...foto, origem: ORIGEM_ARQUIVO.APP_INSPETOR }
              ));
              setValues((prev) => ({
                ...prev,
                fotos: mergeFotos((prev.fotos || []).filter(isFotoRfg), withOrigem),
              }));
            }}
          />
        </Grid>
        {errors && errors.fotos && (
          <Grid size={{ xs: 12 }}>
            <Typography color="error" variant="caption">*Imagens são obrigatórias</Typography>
          </Grid>
        )}
      </Grid>
    </DialogDefaultV2>
  );
};

AuditoriaFaixaModal.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onConfirmAndNext: PropTypes.func,
  open: PropTypes.bool.isRequired,
  selectedId: PropTypes.string,
  nsmps: PropTypes.array.isRequired,
  nsmpSelected: PropTypes.object,
  loadNsmpById: PropTypes.func.isRequired,
  readOnly: PropTypes.bool.isRequired,
  userRole: PropTypes.string,
  userId: PropTypes.string,
  nsirStatus: PropTypes.string,
  nsirData: PropTypes.object.isRequired,
  nsirTitle: PropTypes.string.isRequired,
  onNext: PropTypes.func,
  onPrevious: PropTypes.func,
  hasNext: PropTypes.bool,
  hasPrevious: PropTypes.bool,
  carouselPosition: PropTypes.object,
};

AuditoriaFaixaModal.defaultProps = {
  selectedId: undefined,
  nsmpSelected: {},
};

const mapStateToProps = (state) => ({
  nsmps: selectorsNsmp.getNsmps(state),
  nsmpSelected: selectorsNsmp.getNsmpSelected(state),
  userRole: selectorsAuth.getRole(state),
  userId: selectorsAuth.getUserId(state),
  nsirStatus: selectorsNsir.getNsirStatus(state),
  nsirData: selectorsNsir.getNsirSelected(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ ...actionsNsmp }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(AuditoriaFaixaModal);
